import { api } from '../api';
import type { UserRole } from '../types';

export interface DocumentoSubidoPor {
  id: string;
  nombre: string;
  email: string;
  rol: UserRole;
}

/**
 * Un archivo del share. **El identificador es la RUTA**, no un id: la fuente de
 * verdad es el filesystem de Medicus y ahí no hay ids estables (si alguien
 * renombra el archivo por la red, un id nuestro quedaría colgado).
 */
export interface Archivo {
  /** Ruta relativa a la raíz de la organización (`OP/Retenciones/nota.pdf`). */
  ruta: string;
  nombre: string;
  extension: string;
  tamano: number;
  /** Última modificación en el share. */
  modificadoAt: string;
  /** Sólo si el archivo se subió por el micrositio (metadato decorativo). */
  descripcion?: string;
  subidoPor?: DocumentoSubidoPor;
}

export interface Carpeta {
  ruta: string;
  nombre: string;
  modificadoAt: string;
}

export interface ContenidoCarpeta {
  empresaId: string;
  /** Carpeta listada (`''` = raíz de la organización). */
  ruta: string;
  carpetas: Carpeta[];
  archivos: Archivo[];
  /** Entradas del share que no se listan (symlinks, etc.), con el motivo. */
  omitidos: { nombre: string; motivo: string }[];
}

/** Extensiones permitidas al SUBIR (espejo de DOC_ALLOWED_EXT del backend). */
export const DOC_ACCEPT = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.xlsm',
  '.csv',
  '.txt',
  '.ppt',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.zip',
  '.rar',
  '.7z',
];

/*
 * La organización viaja SIEMPRE por el interceptor de `api.ts`, que manda como
 * `X-Empresa-Id` la activa del store (`reconciliarEmpresaActiva` la deja fijada
 * también para Medicus con una sola organización). Estas funciones no reciben
 * la organización: un header explícito acá sería pisado por el interceptor, y
 * cuando la activa es "Todas" la pantalla directamente no consulta.
 */

/** Contenido de una carpeta del share (`ruta` vacía = raíz de la organización). */
export async function listContenido(ruta?: string): Promise<ContenidoCarpeta> {
  const { data } = await api.get<ContenidoCarpeta>('/documentacion', {
    params: ruta ? { ruta } : {},
  });
  return data;
}

export async function subirArchivo(args: {
  file: File;
  ruta?: string;
  descripcion?: string;
  sobrescribir?: boolean;
}): Promise<Archivo> {
  const fd = new FormData();
  fd.append('file', args.file);
  if (args.descripcion?.trim()) fd.append('descripcion', args.descripcion.trim());
  if (args.ruta) fd.append('ruta', args.ruta);
  if (args.sobrescribir) fd.append('sobrescribir', 'true');
  const { data } = await api.post<Archivo>('/documentacion', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function eliminarArchivo(ruta: string): Promise<void> {
  await api.delete('/documentacion', { params: { ruta } });
}

// ─── Carpetas (directorios reales del share) ───

export async function crearCarpeta(nombre: string, ruta?: string): Promise<Carpeta> {
  const { data } = await api.post<Carpeta>('/documentacion/carpetas', {
    nombre,
    ruta: ruta ?? '',
  });
  return data;
}

export async function renombrarCarpeta(ruta: string, nombre: string): Promise<Carpeta> {
  const { data } = await api.patch<Carpeta>('/documentacion/carpetas', { ruta, nombre });
  return data;
}

export async function eliminarCarpeta(ruta: string): Promise<void> {
  await api.delete('/documentacion/carpetas', { params: { ruta } });
}

/**
 * Descarga el binario vía axios (responseType blob) y dispara la descarga con
 * un blob URL. El nombre sale del Content-Disposition del server.
 */
export async function descargarArchivo(archivo: Archivo): Promise<void> {
  const res = await api.get('/documentacion/descargar', {
    params: { ruta: archivo.ruta },
    responseType: 'blob',
  });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const filename = match?.[1] ?? archivo.nombre;
  const tipo = (res.headers['content-type'] as string | undefined) ?? 'application/octet-stream';

  const blob = new Blob([res.data], { type: tipo });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
